import { useState, useMemo } from 'react';
import {
  Stack,
  Paper,
  Table,
  Text,
  TextInput,
  Skeleton,
  Badge,
} from '@mantine/core';
import { IconSearch, IconCalendarDollar } from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import EmptyState from '@/components/EmptyState';
import { useCustomerFuturePrices } from '@/hooks/usePortalApi';
import type { FuturePrice } from '@/types';
import dayjs from 'dayjs';

type SortField = 'product_name' | 'product_sku' | 'price' | 'effective_date' | 'expiration_date';

export default function FuturePrices() {
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('effective_date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const { data, isLoading } = useCustomerFuturePrices();
  const items: FuturePrice[] = data?.items ?? [];

  const filtered = useMemo(() => {
    let result = [...items];
    if (search) {
      const s = search.toLowerCase();
      result = result.filter(
        (r) =>
          (r.product_name ?? '').toLowerCase().includes(s) ||
          (r.product_sku ?? '').toLowerCase().includes(s),
      );
    }
    result.sort((a, b) => {
      const av = a[sortField];
      const bv = b[sortField];
      const cmp = (av ?? '') < (bv ?? '') ? -1 : (av ?? '') > (bv ?? '') ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return result;
  }, [items, search, sortField, sortDir]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortIndicator = (field: SortField) =>
    sortField === field ? (sortDir === 'asc' ? ' \u2191' : ' \u2193') : '';

  return (
    <Stack gap="lg">
      <PageHeader
        title="Future Prices"
        description="Upcoming price changes for your contracted products"
      />

      <TextInput
        placeholder="Search by product name or SKU..."
        leftSection={<IconSearch size={16} />}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
      />

      {isLoading ? (
        <Stack gap="xs">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} height={50} radius="md" />
          ))}
        </Stack>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<IconCalendarDollar size={30} />}
          title="No future prices"
          description={
            search
              ? 'No items match your search criteria'
              : 'There are no upcoming price changes scheduled'
          }
        />
      ) : (
        <Paper>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('product_name')}
                >
                  Product Name{sortIndicator('product_name')}
                </Table.Th>
                <Table.Th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('product_sku')}
                >
                  SKU{sortIndicator('product_sku')}
                </Table.Th>
                <Table.Th
                  ta="right"
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('price')}
                >
                  Price{sortIndicator('price')}
                </Table.Th>
                <Table.Th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('effective_date')}
                >
                  Effective Date{sortIndicator('effective_date')}
                </Table.Th>
                <Table.Th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSort('expiration_date')}
                >
                  Expires At{sortIndicator('expiration_date')}
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filtered.map((row) => {
                const isUpcoming = dayjs(row.effective_date).isAfter(dayjs());
                return (
                  <Table.Tr key={row.id}>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {row.product_name ?? '\u2014'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {row.product_sku ?? '\u2014'}
                      </Text>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Text size="sm" fw={500}>
                        ${row.price.toFixed(2)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {dayjs(row.effective_date).format('MMM D, YYYY')}
                      </Text>
                      {isUpcoming && (
                        <Badge size="xs" variant="light" color="blue" ml={4}>
                          upcoming
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {row.expiration_date
                          ? dayjs(row.expiration_date).format('MMM D, YYYY')
                          : '\u2014'}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Paper>
      )}
    </Stack>
  );
}
